import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'github-markdown-css';
import { ArrowLeft, Calendar, Edit2, Trash2, Clock } from 'lucide-react';

function BlogDetailsPage({
  darkMode,
  selectedPost,
  setActivePage,
  formatDate,
  editPost,
  deletePost,
  isAdmin,
}) {
  const themeClasses = {
    background: darkMode ? 'bg-gray-900' : 'bg-gray-50',
    card: darkMode ? 'bg-gray-800' : 'bg-white',
    text: darkMode ? 'text-gray-100' : 'text-gray-800',
    subtext: darkMode ? 'text-gray-400' : 'text-gray-600',
    accent: darkMode ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700' : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600',
    backBtn: darkMode ? 'bg-gray-800/70 hover:bg-gray-700 text-gray-300' : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200',
    editBtn: darkMode ? 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700' : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600',
    deleteBtn: darkMode ? 'bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700' : 'bg-gradient-to-r from-rose-500 to-red-500 hover:from-rose-600 hover:to-red-600',
  };

  const getCategoryBadgeColor = (category) => {
    const categories = {
      'Technology': darkMode ? 'bg-indigo-900/70 text-indigo-300 border border-indigo-700' : 'bg-indigo-100 text-indigo-800 border border-indigo-200',
      'Design': darkMode ? 'bg-purple-900/70 text-purple-300 border border-purple-700' : 'bg-purple-100 text-purple-800 border border-purple-200',
      'Business': darkMode ? 'bg-emerald-900/70 text-emerald-300 border border-emerald-700' : 'bg-emerald-100 text-emerald-800 border border-emerald-200',
      'Personal': darkMode ? 'bg-amber-900/70 text-amber-300 border border-amber-700' : 'bg-amber-100 text-amber-800 border border-amber-200',
      'Other': darkMode ? 'bg-gray-700/70 text-gray-300 border border-gray-600' : 'bg-gray-200 text-gray-800 border border-gray-300'
    };
    return categories[category] || categories['Other'];
  };

  if (!selectedPost) {
    return (
      <div className={`${themeClasses.background} min-h-screen py-12 px-4 sm:px-6`}>
        <div className="max-w-4xl mx-auto">
          <div className={`p-16 text-center rounded-2xl ${themeClasses.card} shadow-2xl border fade-in-up ${darkMode ? 'border-gray-700/50' : 'border-gray-100'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-24 w-24 mx-auto mb-6 text-indigo-400 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className={`text-3xl font-bold mb-4 ${themeClasses.text}`}>No post selected.</p>
            <p className={`text-lg mb-8 ${themeClasses.subtext}`}>Please select a blog post to read.</p>
            <button
              onClick={() => setActivePage('blog')}
              className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 shadow-xl hover:scale-105 hover-lift inline-flex items-center ${themeClasses.accent} text-white`}
            >
              <ArrowLeft className="mr-2 w-5 h-5" />
              Back to Blog
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${themeClasses.background} min-h-screen py-8 px-4 sm:px-6`}>
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => setActivePage('blog')}
          className={`mb-8 px-5 py-3 rounded-xl font-medium transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 inline-flex items-center fade-in-down ${themeClasses.backBtn}`}
        >
          <ArrowLeft className="mr-2 w-5 h-5" />
          Back to Blog
        </button>

        {/* Article Card */}
        <article className={`rounded-2xl overflow-hidden shadow-2xl border fade-in-up ${themeClasses.card} ${darkMode ? 'border-gray-700/50' : 'border-gray-100'}`}>
          {/* Gradient Header Bar */}
          <div className="h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>

          {/* Content */}
          <div className="p-8 sm:p-12">
            {/* Category Badge & Meta Info */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className={`px-4 py-2 text-sm font-semibold rounded-full shadow-sm ${getCategoryBadgeColor(selectedPost.category)}`}>
                {selectedPost.category}
              </span>
              <div className="flex items-center gap-4 text-sm">
                <div className={`flex items-center gap-2 ${themeClasses.subtext}`}>
                  <Calendar className="w-4 h-4" />
                  <span className="font-medium">{formatDate(selectedPost.date)}</span>
                </div>
                {selectedPost.edited && (
                  <>
                    <span className={`w-1.5 h-1.5 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></span>
                    <div className={`flex items-center gap-2 ${themeClasses.subtext}`}>
                      <Clock className="w-4 h-4" />
                      <span>Edited: {formatDate(selectedPost.edited)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Title */}
            <h1 className={`text-4xl sm:text-5xl font-extrabold mb-8 leading-tight ${themeClasses.text}`}>
              {selectedPost.title}
            </h1>

            {/* Divider */}
            <div className={`h-px mb-10 ${darkMode ? 'bg-gradient-to-r from-transparent via-gray-700 to-transparent' : 'bg-gradient-to-r from-transparent via-gray-300 to-transparent'}`}></div>

            {/* Article Content */}
            <div
              className={`markdown-body prose prose-lg max-w-none ${darkMode ? 'prose-invert' : ''}`}
              style={{
                color: darkMode ? 'white' : 'black',
                backgroundColor: 'transparent',
                border: 'none',
                padding: 0,
                lineHeight: '1.8',
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {selectedPost.content}
              </ReactMarkdown>
            </div>

            {/* Admin Actions */}
            {isAdmin && (
              <>
                {/* Divider */}
                <div className={`h-px my-10 ${darkMode ? 'bg-gradient-to-r from-transparent via-gray-700 to-transparent' : 'bg-gradient-to-r from-transparent via-gray-300 to-transparent'}`}></div>

                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={() => editPost(selectedPost.id)}
                    className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:scale-105 hover-lift inline-flex items-center ${themeClasses.editBtn} text-white`}
                  >
                    <Edit2 className="mr-2 w-5 h-5" />
                    Edit Post
                  </button>
                  <button
                    onClick={() => deletePost(selectedPost.id)}
                    className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:scale-105 hover-lift inline-flex items-center ${themeClasses.deleteBtn} text-white`}
                  >
                    <Trash2 className="mr-2 w-5 h-5" />
                    Delete Post
                  </button>
                </div>
              </>
            )}
          </div>
        </article>

        {/* Bottom Spacing */}
        <div className="h-12"></div>
      </div>
    </div>
  );
}

export default BlogDetailsPage;
